/**
 * Borrower Address Editor — view + modify the primary address on a borrower.
 *
 * Mirrors PropertyAddressEditor's UX (view card → "Modify" dropdown →
 * Search/Manual toggle → geocode-verified badge) but targets the borrower
 * address endpoints in useAddressHook:
 *   - Autocomplete pick → POST /property_address/borrower/create_from_string/{dealId}/{borrowerId}
 *   - Manual entry     → POST /property_address/borrower/create_from_object/{dealId}/{borrowerId}
 *   - Re-attach unit   → POST /property_address/borrower/update/{dealId}/{borrowerId}
 *
 * Notes:
 *   - A residence can carry a unit/apt number, which the geocoder won't
 *     return. After create_from_string (which whole-replaces the address),
 *     we re-attach the user-typed unit via update so it survives.
 *   - Like PropertyAddressEditor, this is intentionally NOT bound to
 *     react-final-form. It owns its own state and dispatches
 *     loanDrilldownSet on success, keeping the address-edit flow decoupled
 *     from the borrower's per-section edit form.
 *   - Uses AddressMap (the borrower sidebar's existing map) rather than
 *     PropertyMap, whose screenshot-to-loan flow is property-specific.
 */
import {
  CheckCircleOutline,
  CloseOutlined,
  EditLocationAltOutlined,
  EditOutlined,
  HomeOutlined,
  KeyboardArrowDownOutlined,
  LocationOffOutlined,
  LocationOnOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { amber, blue, green, grey } from "@mui/material/colors";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { formatAddressObjectFullAddress } from "../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import { selectOptionsUsaStates } from "../../../constants/selectOptions/selectOptionsUsaStates";
import { useAddressHook } from "../../../hooks/useAddressHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import AddressMap from "../../common/AddressMap";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

const PLACES_AUTOCOMPLETE_URL =
  "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_DETAILS_URL = "https://places.googleapis.com/v1/places";

const getComponent = (components, type) =>
  components?.find((c) => c.types?.includes(type));
const short = (components, type) =>
  getComponent(components, type)?.shortText || "";
const long = (components, type) =>
  getComponent(components, type)?.longText || "";

const placeToFields = (place) => {
  const c = place?.addressComponents || [];
  return {
    streetNumber: short(c, "street_number"),
    streetName: long(c, "route"),
    city:
      long(c, "locality") || long(c, "sublocality") || long(c, "postal_town"),
    state: short(c, "administrative_area_level_1"),
    zip: short(c, "postal_code"),
    fullAddress: place?.formattedAddress || "",
  };
};

const emptyFields = (savedAddress) => ({
  streetNumber: savedAddress?.streetNumber || "",
  streetName: savedAddress?.streetName || "",
  city: savedAddress?.city || "",
  state: savedAddress?.state || "",
  zip: savedAddress?.zip || "",
});

const BorrowerAddressEditor = ({ borrower, loanId }) => {
  const dispatch = useDispatch();
  const {
    createBorrowerAddressFromString,
    createBorrowerAddressFromObject,
    updateBorrowerAddress,
    loading: addressSaving,
  } = useAddressHook();
  const [savingUnit, setSavingUnit] = useState(false);
  const saving = addressSaving || savingUnit;

  const borrowerId = borrower?._id;
  const savedAddress = borrower?.primaryAddress;

  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [editMode, setEditMode] = useState("autocomplete"); // "autocomplete" | "manual"
  const [error, setError] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null); // Modify dropdown anchor

  // Autocomplete state
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null); // Google-resolved fields after a pick

  // Always-visible unit number while editing — geocoder won't return one,
  // so we collect it client-side and re-attach via update after the
  // whole-replace endpoints run.
  const [unitNumber, setUnitNumber] = useState("");

  // Manual piece-wise fields (used when editMode === "manual")
  const [manualFields, setManualFields] = useState(emptyFields(savedAddress));

  const googleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const resetEditingState = useCallback(() => {
    // Seed the search box with the current saved address so the user can
    // see / tweak it instead of starting from a blank field.
    const savedDisplay = formatAddressObjectFullAddress(savedAddress, true);
    setInputValue(savedDisplay && savedDisplay !== "N/A" ? savedDisplay : "");
    setOptions([]);
    setPicked(null);
    setUnitNumber(savedAddress?.addressUnitNumber || "");
    setManualFields(emptyFields(savedAddress));
    setError(null);
  }, [savedAddress]);

  // Entry point from the Modify dropdown — `startMode` is "autocomplete"
  // ("Enter new address") or "manual" ("Enter manually").
  const handleStartEditing = useCallback(
    (startMode) => {
      resetEditingState();
      setEditMode(startMode);
      setMode("edit");
      setMenuAnchor(null);
    },
    [resetEditingState],
  );

  const handleCancel = useCallback(() => {
    resetEditingState();
    setMode("view");
  }, [resetEditingState]);

  // ── Google Places autocomplete ────────────────────────────────────
  const searchAddresses = useCallback(
    async (query) => {
      if (!query || query.length < 3 || !googleMapsKey) {
        setOptions([]);
        return;
      }
      setSearching(true);
      try {
        const res = await fetch(PLACES_AUTOCOMPLETE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": googleMapsKey,
          },
          body: JSON.stringify({
            input: query,
            includedRegionCodes: ["us"],
            includedPrimaryTypes: [
              "street_address",
              "premise",
              "subpremise",
              "route",
            ],
          }),
        });
        const data = await res.json();
        const suggestions = (data.suggestions || [])
          .map((s) => s.placePrediction)
          .filter(Boolean)
          .map((p) => ({
            id: p.placeId,
            placeId: p.placeId,
            fullAddress: p.text?.text || "",
            mainText: p.structuredFormat?.mainText?.text || "",
            secondaryText: p.structuredFormat?.secondaryText?.text || "",
          }));
        setOptions(suggestions);
      } catch (err) {
        console.error("Places autocomplete error", err);
        setOptions([]);
      } finally {
        setSearching(false);
      }
    },
    [googleMapsKey],
  );

  const debouncedSearch = useMemo(
    () => debounce(searchAddresses, 350),
    [searchAddresses],
  );

  useEffect(() => {
    if (mode === "edit" && editMode === "autocomplete") {
      debouncedSearch(inputValue);
    }
    return () => debouncedSearch.cancel();
  }, [inputValue, mode, editMode, debouncedSearch]);

  const fetchPlaceDetails = useCallback(
    async (placeId) => {
      const res = await fetch(`${PLACES_DETAILS_URL}/${placeId}`, {
        method: "GET",
        headers: {
          "X-Goog-Api-Key": googleMapsKey,
          "X-Goog-FieldMask": "id,formattedAddress,addressComponents",
        },
      });
      return res.json();
    },
    [googleMapsKey],
  );

  const handlePick = useCallback(
    async (_event, option) => {
      if (!option || !option.placeId) return;
      setSearching(true);
      try {
        const place = await fetchPlaceDetails(option.placeId);
        const fields = placeToFields(place);
        setPicked(fields);
        setInputValue(fields.fullAddress || option.fullAddress);
      } catch (err) {
        console.error("Place details error", err);
        setError("Couldn't load place details. Try again.");
      } finally {
        setSearching(false);
      }
    },
    [fetchPlaceDetails],
  );

  // ── Save handlers ─────────────────────────────────────────────────
  const handleSuccess = useCallback(
    (response) => {
      if (response) {
        dispatch(loanDrilldownSet(response));
        dispatch(loanPipelineClear(response));
      }
      setMode("view");
      setError(null);
    },
    [dispatch],
  );

  const reattachUnitIfNeeded = useCallback(
    (response) => {
      const trimmedUnit = (unitNumber || "").trim();
      if (!trimmedUnit) {
        handleSuccess(response);
        return;
      }
      // Send only the unit — the backend preserves sibling fields, so we
      // don't clobber the just-saved geocode result.
      setSavingUnit(true);
      updateBorrowerAddress({
        dealId: loanId,
        borrowerId,
        addressObject: { addressUnitNumber: trimmedUnit, unit: trimmedUnit },
        onSuccessFn: (resp) => {
          setSavingUnit(false);
          handleSuccess(resp);
        },
        onFailFn: () => {
          setSavingUnit(false);
          setError(
            "Address saved, but the unit number didn't stick. You can edit it again.",
          );
        },
      });
    },
    [unitNumber, updateBorrowerAddress, loanId, borrowerId, handleSuccess],
  );

  const handleSaveAutocomplete = useCallback(() => {
    if (!picked) {
      setError("Pick an address from the suggestions, or switch to manual.");
      return;
    }
    setError(null);
    createBorrowerAddressFromString({
      dealId: loanId,
      borrowerId,
      addressString: picked.fullAddress,
      onSuccessFn: reattachUnitIfNeeded,
      onFailFn: () =>
        setError(
          "Couldn't save the address. Try again or use manual entry to skip the geocoder.",
        ),
    });
  }, [
    picked,
    createBorrowerAddressFromString,
    loanId,
    borrowerId,
    reattachUnitIfNeeded,
  ]);

  const handleSaveManual = useCallback(() => {
    // State may still be an { label, value } option object from an older
    // render (HMR keeps component state) — normalize before .trim().
    const f = {
      ...manualFields,
      state:
        typeof manualFields.state === "string"
          ? manualFields.state
          : manualFields.state?.value || "",
    };
    if (!f.streetNumber.trim() || !f.streetName.trim()) {
      setError("Street number and street name are required.");
      return;
    }
    if (!f.city.trim() || !f.state.trim() || !f.zip.trim()) {
      setError("City, state, and ZIP are required.");
      return;
    }
    setError(null);
    const addressObject = {
      streetNumber: f.streetNumber.trim(),
      streetName: f.streetName.trim(),
      city: f.city.trim(),
      state: f.state.trim().toUpperCase(),
      zip: f.zip.trim(),
    };
    const trimmedUnit = (unitNumber || "").trim();
    if (trimmedUnit) {
      addressObject.addressUnitNumber = trimmedUnit;
      addressObject.unit = trimmedUnit;
    }
    createBorrowerAddressFromObject({
      dealId: loanId,
      borrowerId,
      addressObject,
      onSuccessFn: handleSuccess,
      onFailFn: () =>
        setError(
          "Couldn't save the address. Double-check the fields and try again.",
        ),
    });
  }, [
    manualFields,
    unitNumber,
    createBorrowerAddressFromObject,
    loanId,
    borrowerId,
    handleSuccess,
  ]);

  const handleSwitchToManual = useCallback(() => {
    // Carry over any autocomplete-resolved fields so the manual form
    // isn't blank if the user picked a suggestion first.
    if (picked) {
      setManualFields((prev) => ({
        streetNumber: picked.streetNumber || prev.streetNumber,
        streetName: picked.streetName || prev.streetName,
        city: picked.city || prev.city,
        state: picked.state || prev.state,
        zip: picked.zip || prev.zip,
      }));
    }
    setEditMode("manual");
  }, [picked]);

  // ── Render ────────────────────────────────────────────────────────
  const displayString = useMemo(
    () => formatAddressObjectFullAddress(savedAddress, true),
    [savedAddress],
  );
  const hasSavedAddress = displayString && displayString !== "N/A";
  const failedGeocode =
    savedAddress?.geolocation === "failed" ||
    (savedAddress && !savedAddress.latitude && !savedAddress.longitude);

  return (
    <Flx column g={1.5}>
      {hasSavedAddress && (
        <Box
          sx={{
            position: "relative",
            border: `1px solid ${grey[300]}`,
            overflow: "hidden",
            borderRadius: "8px",
            backgroundColor: grey[200],
            height: "180px",
          }}
        >
          <AddressMap height="100%" sx={{ zIndex: 111 }} address={savedAddress} />
        </Box>
      )}

      {mode === "view" ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Flx
            center
            sx={{
              width: 38,
              height: 38,
              borderRadius: "9px",
              background: hasSavedAddress ? blue[50] : grey[100],
              flexShrink: 0,
            }}
          >
            <LocationOnOutlined
              sx={{
                fontSize: 20,
                color: hasSavedAddress ? blue[700] : grey[400],
              }}
            />
          </Flx>
          <Flx column g={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: hasSavedAddress ? "#0f172a" : grey[500],
                fontStyle: hasSavedAddress ? "normal" : "italic",
                wordBreak: "break-word",
                lineHeight: 1.35,
              }}
            >
              {hasSavedAddress ? displayString : "No address set"}
            </Txt>
            {savedAddress?.addressUnitNumber && (
              <Txt sx={{ fontSize: 12, color: grey[600] }}>
                Unit {savedAddress.addressUnitNumber}
              </Txt>
            )}
            {hasSavedAddress &&
              (failedGeocode ? (
                <Flx ac g={0.5} sx={{ mt: 0.25 }}>
                  <LocationOffOutlined
                    sx={{ fontSize: "13px !important", color: amber[800] }}
                  />
                  <Txt sx={{ fontSize: 11, color: amber[800] }}>
                    Not geocode-verified
                  </Txt>
                </Flx>
              ) : (
                <Flx ac g={0.5} sx={{ mt: 0.25 }}>
                  <CheckCircleOutline
                    sx={{ fontSize: "13px !important", color: green[700] }}
                  />
                  <Txt sx={{ fontSize: 11, color: green[700] }}>
                    Geocode verified
                  </Txt>
                </Flx>
              ))}
          </Flx>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditLocationAltOutlined className="thin" />}
            endIcon={<KeyboardArrowDownOutlined />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Modify
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => handleStartEditing("autocomplete")}>
              <ListItemIcon>
                <SearchOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Enter new address"
                secondary="Search and pick a verified address"
              />
            </MenuItem>
            <MenuItem onClick={() => handleStartEditing("manual")}>
              <ListItemIcon>
                <EditOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Enter manually"
                secondary="Type the address fields yourself"
              />
            </MenuItem>
          </Menu>
        </Box>
      ) : (
        <Box
          sx={{
            border: `1px solid ${grey[200]}`,
            borderRadius: "10px",
            background: "#fff",
            overflow: "hidden",
          }}
        >
          <Flx
            ac
            jb
            sx={{
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${grey[100]}`,
              background: grey[50],
            }}
          >
            <Flx ac g={0.75}>
              <EditLocationAltOutlined sx={{ fontSize: 17, color: grey[700] }} />
              <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                Modify Address
              </Txt>
            </Flx>
            <Tooltip title="Cancel" arrow>
              <IconButton
                size="small"
                onClick={handleCancel}
                disabled={saving}
                sx={{ p: 0.25 }}
              >
                <CloseOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Flx>

          <Flx column g={1.5} sx={{ p: 1.5 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={editMode}
              onChange={(_e, val) => {
                if (!val) return;
                if (val === "manual") handleSwitchToManual();
                else setEditMode("autocomplete");
              }}
            >
              <ToggleButton
                value="autocomplete"
                sx={{ textTransform: "none", gap: 0.5 }}
              >
                <SearchOutlined sx={{ fontSize: 16 }} />
                Search
              </ToggleButton>
              <ToggleButton
                value="manual"
                sx={{ textTransform: "none", gap: 0.5 }}
              >
                <EditOutlined sx={{ fontSize: 16 }} />
                Manual
              </ToggleButton>
            </ToggleButtonGroup>

            {editMode === "autocomplete" ? (
              <Flx column g={1}>
                <Autocomplete
                  freeSolo
                  filterOptions={(x) => x}
                  options={options}
                  loading={searching}
                  inputValue={inputValue}
                  onInputChange={(_e, val, reason) => {
                    if (reason === "input" || reason === "clear") {
                      setInputValue(val);
                      if (picked) setPicked(null);
                    }
                  }}
                  onChange={handlePick}
                  getOptionLabel={(opt) =>
                    typeof opt === "string" ? opt : opt?.fullAddress || ""
                  }
                  isOptionEqualToValue={(opt, value) =>
                    opt?.placeId === value?.placeId
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.placeId}>
                      <Box>
                        <Txt sx={{ fontSize: 13, fontWeight: 500 }}>
                          {option.mainText || option.fullAddress}
                        </Txt>
                        {option.secondaryText && (
                          <Txt sx={{ fontSize: 11.5, color: grey[600] }}>
                            {option.secondaryText}
                          </Txt>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Search borrower address"
                      placeholder="Start typing an address…"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchOutlined
                              sx={{ fontSize: 18, color: grey[500] }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {picked && (
                  <Alert
                    icon={<HomeOutlined sx={{ fontSize: "16px !important" }} />}
                    severity="info"
                    sx={{ alignItems: "center", py: 0.5 }}
                  >
                    Selected: <strong>{picked.fullAddress}</strong>
                  </Alert>
                )}
                <TextField
                  size="small"
                  label="Unit / Apt / Suite (optional)"
                  placeholder="e.g. Apt 4B"
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                />
              </Flx>
            ) : (
              <Flx column g={1}>
                <Flx g={1} sx={{ flexWrap: "wrap" }}>
                  <Box sx={{ flex: "0 0 110px" }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Street Number"
                      value={manualFields.streetNumber}
                      onChange={(e) =>
                        setManualFields((prev) => ({
                          ...prev,
                          streetNumber: e.target.value,
                        }))
                      }
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 160 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Street Name"
                      value={manualFields.streetName}
                      onChange={(e) =>
                        setManualFields((prev) => ({
                          ...prev,
                          streetName: e.target.value,
                        }))
                      }
                    />
                  </Box>
                </Flx>
                <TextField
                  size="small"
                  label="Unit / Apt / Suite (optional)"
                  placeholder="e.g. Apt 4B"
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                />
                <Flx g={1} sx={{ flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: 140 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="City"
                      value={manualFields.city}
                      onChange={(e) =>
                        setManualFields((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                    />
                  </Box>
                  <Box sx={{ flex: "0 0 100px" }}>
                    <Autocomplete
                      size="small"
                      options={selectOptionsUsaStates}
                      value={manualFields.state || null}
                      // Options are { label, value } — keep the 2-letter code
                      // as a string so the save handler's .trim() works.
                      onChange={(_e, val) =>
                        setManualFields((prev) => ({
                          ...prev,
                          state:
                            (typeof val === "string" ? val : val?.value) || "",
                        }))
                      }
                      getOptionLabel={(opt) =>
                        typeof opt === "string" ? opt : opt?.value || opt || ""
                      }
                      isOptionEqualToValue={(opt, val) => {
                        const a = typeof opt === "string" ? opt : opt?.value;
                        const b = typeof val === "string" ? val : val?.value;
                        return a === b;
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="State" />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: "0 0 110px" }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="ZIP"
                      value={manualFields.zip}
                      onChange={(e) =>
                        setManualFields((prev) => ({
                          ...prev,
                          zip: e.target.value,
                        }))
                      }
                    />
                  </Box>
                </Flx>
                <Txt sx={{ fontSize: 11.5, color: grey[600] }}>
                  The backend will attempt to geocode these values. If no
                  match, the address still saves with what you entered.
                </Txt>
              </Flx>
            )}

            {error && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {error}
              </Alert>
            )}

            <Divider sx={{ mx: -1.5 }} />

            <Flx g={1} jr ac>
              <Button
                size="small"
                variant="text"
                onClick={handleCancel}
                disabled={saving}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={
                  editMode === "manual"
                    ? handleSaveManual
                    : handleSaveAutocomplete
                }
                disabled={saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : null
                }
                sx={{ textTransform: "none" }}
              >
                {saving ? "Saving…" : "Save Address"}
              </Button>
            </Flx>
          </Flx>
        </Box>
      )}
    </Flx>
  );
};

export default BorrowerAddressEditor;
